import * as arrow from "apache-arrow";
import ParquetWorker from "./parquet.worker.ts?worker";

/**
 * Worker manager for parsing parquet files in parallel.
 * Creates one worker per table to enable true parallelism.
 *
 * Parquet workers decode parquet bytes via WASM and return an Arrow IPC stream.
 * The manager reconstructs the Arrow table on the main thread via tableFromIPC
 * (fast for the small aggregated datasets returned by view transforms).
 */

/**
 * Parquet worker pool size (ported from the dashmart frontend, where the pool
 * was a large interactive win). The /shipments page fans out several VTs at once
 * — cold load (FR summary + sku summary + schema×2 + mapping gaps), save-refetch
 * (refreshDataStreams fires 3–4 VTs), grain switches, and detail-on-expand — and
 * a single shared worker serializes ALL of them. A small pool lets that burst
 * parse in parallel. Warmed at app shell (App.tsx) so each worker's one-time
 * ~6 MB WASM compile overlaps the auth + VT server round-trip rather than the
 * parse, keeping the extra compiles off the critical path. Supersedes the
 * CHO-617 single-worker choice (which optimized cold-load to a single compile at
 * the cost of serializing every later interaction).
 */
const MAX_PARQUET_WORKERS = 4;

type WorkerResponse =
  | {
      id: string;
      type: "PARSE_SUCCESS";
      payload: {
        ipcStream: ArrayBuffer;
        columns: string[];
        numRows: number;
        // Opaque result of an optional in-worker post-process (e.g. SkuRow[]
        // materialization). Threaded through generically; the caller casts.
        postProcessResult?: unknown;
      };
    }
  | { id: string; type: "PARSE_ERROR"; payload: { error: string } };

export interface ParseResult {
  arrowTable: arrow.Table;
  columns: string[];
  postProcessResult?: unknown;
}

class ParquetWorkerManager {
  private workers: Worker[] = [];
  private workerIndex = 0;
  private pendingTasks = new Map<
    string,
    {
      resolve: (result: ParseResult) => void;
      reject: (error: Error) => void;
    }
  >();

  /**
   * Create a dedicated worker for a specific parse task.
   * Each table gets its own worker so they can load independently.
   */
  private createDedicatedWorker(): Worker {
    try {
      const worker = new ParquetWorker({ type: "module" } as WorkerOptions);

      worker.addEventListener("message", this.handleWorkerMessage.bind(this));

      worker.addEventListener("error", (error) => {
        console.error("[ParquetWorkerManager] Worker error:", error);
        const index = this.workers.indexOf(worker);
        if (index > -1) {
          this.workers.splice(index, 1);
        }
      });

      worker.addEventListener("messageerror", (error) => {
        console.error("[ParquetWorkerManager] Worker message error:", error);
      });

      this.workers.push(worker);
      return worker;
    } catch (error) {
      console.error("[ParquetWorkerManager] Failed to create worker:", error);
      throw new Error(
        `Failed to create Web Worker: ${error instanceof Error ? error.message : String(error)}. ` +
          `This might indicate a build configuration issue. Check browser console for worker loading errors.`
      );
    }
  }

  private handleWorkerMessage(event: MessageEvent<WorkerResponse>) {
    const { id, type, payload } = event.data;
    const task = this.pendingTasks.get(id);

    if (!task) {
      return;
    }

    this.pendingTasks.delete(id);

    if (type === "PARSE_SUCCESS") {
      try {
        const ipcStream = new Uint8Array(payload.ipcStream);
        const arrowTable = arrow.tableFromIPC(ipcStream);
        task.resolve({
          arrowTable,
          columns: payload.columns,
          postProcessResult: payload.postProcessResult,
        });
      } catch (err) {
        task.reject(
          new Error(
            `Failed to reconstruct Arrow table: ${err instanceof Error ? err.message : String(err)}`
          )
        );
      }
    } else if (type === "PARSE_ERROR") {
      task.reject(new Error(payload.error));
    }
  }

  /**
   * Acquire a worker for a parse task.
   *
   * Grow the pool up to MAX_PARQUET_WORKERS (one worker per concurrent parse),
   * then round-robin reuse. With warm() pre-creating the full pool at app shell,
   * the pool is already populated before the first parse, so this round-robins
   * across warm workers — concurrent VT parses run in parallel instead of
   * serializing. The `< MAX` create branch is the fallback when warm() didn't
   * run or workers died. (See MAX_PARQUET_WORKERS for the cold-load tradeoff vs
   * the prior CHO-617 single-worker design.)
   */
  private async acquireWorker(): Promise<Worker> {
    if (this.workers.length >= MAX_PARQUET_WORKERS) {
      const worker = this.workers[this.workerIndex % this.workers.length];
      this.workerIndex = (this.workerIndex + 1) % this.workers.length;
      return worker;
    }
    // Pool not full yet — create another worker (with a short timeout to surface
    // build/loading failures rather than hang) so the burst parses in parallel.
    return Promise.race([
      Promise.resolve(this.createDedicatedWorker()),
      new Promise<Worker>((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error("Worker creation timeout after 2 seconds - workers may not be loading")
            ),
          2000
        )
      ),
    ]);
  }

  /**
   * Parse parquet from bytes. Reuses a single shared worker so the parquet WASM
   * is fetched and compiled once per session (see acquireWorker).
   */
  async parseFromBytes(bytes: Uint8Array, postProcess?: unknown): Promise<ParseResult> {
    const id = `parse-${Date.now()}-${Math.random()}`;

    let worker: Worker;
    try {
      worker = await this.acquireWorker();
    } catch (error) {
      console.error("[ParquetWorkerManager] Failed to get/create worker:", error);
      throw error;
    }

    return new Promise((resolve, reject) => {
      // Scale timeout by file size
      const fileSizeMB = bytes.length / 1024 / 1024;
      const timeoutMs = fileSizeMB > 50 ? 300000 : fileSizeMB < 5 ? 15000 : 120000;
      const timeout = setTimeout(() => {
        this.pendingTasks.delete(id);
        console.error("[ParquetWorkerManager] Worker parse timeout", {
          bytesLength: bytes.length,
          bytesLengthMB: fileSizeMB.toFixed(2),
          timeoutMs,
          workerCount: this.workers.length,
          pendingTasks: this.pendingTasks.size,
        });

        const workerIdx = this.workers.indexOf(worker);
        if (workerIdx > -1) {
          try {
            worker.terminate();
          } catch { /* ignore */ }
          this.workers.splice(workerIdx, 1);
          console.warn(
            `[ParquetWorkerManager] Terminated stuck worker (index ${workerIdx}), remaining: ${this.workers.length}`
          );
        }

        reject(
          new Error(
            `Worker parse timeout after ${timeoutMs / 1000} seconds - worker may have failed to load or file is too large (${fileSizeMB.toFixed(2)}MB)`
          )
        );
      }, timeoutMs);

      this.pendingTasks.set(id, {
        resolve: (result) => {
          clearTimeout(timeout);
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeout);
          reject(error);
        },
      });

      try {
        const bytesCopy = new Uint8Array(bytes);
        worker.postMessage(
          {
            id,
            type: "PARSE_FROM_BYTES",
            payload: { bytes: bytesCopy, postProcess },
          },
          [bytesCopy.buffer]
        );
      } catch (error) {
        clearTimeout(timeout);
        this.pendingTasks.delete(id);
        reject(
          new Error(
            `Failed to send message to worker: ${error instanceof Error ? error.message : String(error)}`
          )
        );
      }
    });
  }

  /**
   * Warm the worker pool by pre-creating workers (default: the full pool).
   * Call at app shell so each worker's parquet WASM compile runs off the
   * critical path, overlapping auth + bundle parse + the VT server compute — so
   * when the VT fan-out lands, the pool is warm and parses run in parallel with
   * no compile on the critical path. (See MAX_PARQUET_WORKERS.)
   */
  warm(count = MAX_PARQUET_WORKERS): void {
    while (this.workers.length < count) {
      try {
        this.createDedicatedWorker();
      } catch {
        break;
      }
    }
  }

  /** Clean up all workers. */
  terminate(): void {
    this.workers.forEach((worker) => worker.terminate());
    this.workers = [];
    this.pendingTasks.clear();
  }

  /** Global cleanup function for app unmount. */
  static cleanup(): void {
    parquetWorkerManager.terminate();
  }
}

// Singleton instance
export const parquetWorkerManager = new ParquetWorkerManager();
